import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";
import HabitCard from "@/components/habit-card";
import CreateHabitModal from "@/components/create-habit-modal";
import { useQuickCreateRoute } from "@/hooks/useQuickCreateRoute";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Plus, Target, TrendingUp, Flame, Calendar, Grid3x3, List, GripVertical, PartyPopper, HelpCircle, Activity, Layers } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { HabitWithStreakState as Habit, HabitStats } from "@shared/schema";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { format, subDays, eachDayOfInterval, startOfDay } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { utcEntryDay } from "@/lib/trackerx-dates";
import { useTodayDate } from "@/hooks/useTodayDate";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableHabitItem({ habit, viewMode, celebrationEnabled }: { habit: Habit; viewMode: "grid" | "list"; celebrationEnabled: boolean }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: habit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (viewMode === "list") {
    return (
      <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-2" data-testid={`sortable-habit-${habit.id}`}>
        <div 
          {...listeners} 
          className="cursor-grab active:cursor-grabbing p-2 hover:bg-muted rounded touch-none" 
          onClick={(e) => e.stopPropagation()}
          data-testid={`drag-handle-habit-${habit.id}`}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <HabitCard habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <HabitCard habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
    </div>
  );
}

export default function HabitsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useQuickCreateRoute("habit", () => setIsCreateModalOpen(true));
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [streakPeriod, setStreakPeriod] = useState<"weekly" | "monthly" | "quarterly" | "semi-annually" | "annually">("monthly");
  const [celebrationEnabled, setCelebrationEnabled] = useState(true);
  const [celebrationSynced, setCelebrationSynced] = useState(false);
  const [showCelebrationHelp, setShowCelebrationHelp] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [trendsDays, setTrendsDays] = useState(30);
  const [groupBy, setGroupBy] = useState<"none" | "category">("none");
  const [enabledHabits, setEnabledHabits] = useState<Set<string>>(new Set());
  const [habitsInitialized, setHabitsInitialized] = useState(false);
  const isMobile = useIsMobile();

  // Load celebration preference from user-scoped key once userId is known.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(userLsKey("habitCelebrationEnabled", userId));
      if (saved !== null) setCelebrationEnabled(saved === "true");
      if (userId) localStorage.removeItem("habitCelebrationEnabled");
    } catch {}
  }, [userId]);

  // Load persistent filter preferences (groupBy, selectedCategory).
  useEffect(() => {
    try {
      const savedGroupBy = localStorage.getItem(userLsKey("habitsGroupBy", userId));
      if (savedGroupBy === "category" || savedGroupBy === "none") setGroupBy(savedGroupBy);
      const savedCategory = localStorage.getItem(userLsKey("habitsCategory", userId));
      if (savedCategory) setSelectedCategory(savedCategory);
    } catch {}
  }, [userId]);

  // Track today's date in state so that a midnight flip detected on tab focus
  // drives a new query key and forces a fresh fetch automatically.
  const localDate = useTodayDate();

  const { data: habits = [], isLoading: habitsLoading } = useQuery<Habit[]>({
    queryKey: ["/api/habits"],
  });

  // All categories (including goals-scoped) for the filter dropdown
  const { data: allCategories = [] } = useQuery<{ id: string; name: string | null; color: string | null }[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: habitStats, isLoading: statsLoading } = useQuery<HabitStats>({
    queryKey: ["/api/stats/habits", localDate],
    queryFn: () =>
      fetch(`/api/stats/habits?date=${localDate}`, { credentials: "include", cache: "no-store" }).then(r => r.json()),
  });

  const { data: userPrefs } = useQuery<{ celebrationEnabled?: boolean }>({
    queryKey: ["/api/user/preferences"],
  });

  useEffect(() => {
    if (!celebrationSynced && userPrefs !== undefined) {
      const serverValue = userPrefs.celebrationEnabled;
      if (serverValue !== null && serverValue !== undefined) {
        setCelebrationEnabled(serverValue);
        localStorage.setItem(userLsKey("habitCelebrationEnabled", userId), String(serverValue));
      }
      setCelebrationSynced(true);
    }
  }, [userPrefs, celebrationSynced]);

  type TrendEntry = { date: string; completed: boolean };
  type HabitTrend = { habitId: string; habitName: string; frequency: string; category: string; entries: TrendEntry[] };

  const { data: trendsData = [] } = useQuery<HabitTrend[]>({
    queryKey: ["/api/habits/trends", trendsDays],
    queryFn: async () => {
      const res = await fetch(`/api/habits/trends?days=${trendsDays}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch trends');
      return res.json();
    },
    enabled: showTrends,
  });

  useEffect(() => {
    if (trendsData.length > 0 && !habitsInitialized) {
      setEnabledHabits(new Set(trendsData.map(h => h.habitId)));
      setHabitsInitialized(true);
    }
  }, [trendsData, habitsInitialized]);

  const consistencyPercent = useMemo(() => {
    const activeHabits = habits.filter(h => h.isActive);
    if (activeHabits.length === 0) return 0;
    const completionRate = parseInt(String(habitStats?.completionRate || 0));
    return completionRate;
  }, [habits, habitStats]);

  const TREND_COLORS = [
    '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
  ];

  const trendChartData = useMemo(() => {
    if (trendsData.length === 0) return [];
    const end = startOfDay(new Date());
    const start = startOfDay(subDays(end, trendsDays));
    const allDays = eachDayOfInterval({ start, end });

    return allDays.map(day => {
      const point: Record<string, any> = { date: format(day, 'MMM d') };

      trendsData.forEach(habit => {
        if (!enabledHabits.has(habit.habitId)) return;
        const entryDates = new Set(
          habit.entries
            .filter(e => e.completed)
            .map(e => utcEntryDay(e.date))
        );

        let streak = 0;
        for (let d = start; d <= day; d = new Date(d.getTime() + 86400000)) {
          const ds = format(d, 'yyyy-MM-dd');
          if (entryDates.has(ds)) {
            streak++;
          } else {
            streak = 0;
          }
        }
        point[habit.habitId] = streak;
      });

      return point;
    });
  }, [trendsData, enabledHabits, trendsDays]);

  const toggleHabitTrend = (habitId: string) => {
    setEnabledHabits(prev => {
      const next = new Set(prev);
      if (next.has(habitId)) {
        next.delete(habitId);
      } else {
        next.add(habitId);
      }
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const restoreHabitMutation = useMutation({
    mutationFn: async (habitId: string) => {
      return apiRequest("PATCH", `/api/habits/${habitId}`, { isActive: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
    },
  });

  const reorderHabitsMutation = useMutation({
    mutationFn: async (habitIds: string[]) => {
      await apiRequest("PATCH", "/api/habits/reorder", { habitIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
    },
  });

  const handleCelebrationToggle = async (enabled: boolean) => {
    setCelebrationEnabled(enabled);
    localStorage.setItem(userLsKey("habitCelebrationEnabled", userId), String(enabled));
    try {
      await apiRequest("PATCH", "/api/user/preferences", { celebrationEnabled: enabled });
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    } catch (err) {
      console.error("Failed to save celebration preference:", err);
    }
  };

  const filteredHabits = habits.filter(habit => {
    if (selectedCategory === "all") return habit.isActive;
    return habit.isActive && (habit.category ?? "").toLowerCase() === selectedCategory.toLowerCase();
  });

  const archivedHabits = habits.filter(h => !h.isActive);

  // Group active habits by category when groupBy === "category"
  const groupedHabits = useMemo(() => {
    if (groupBy !== "category") return null;
    const groups = new Map<string, Habit[]>();
    for (const habit of filteredHabits) {
      const cat = habit.category?.trim() || "Uncategorized";
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(habit);
    }
    return groups;
  }, [filteredHabits, groupBy]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredHabits.findIndex((h) => h.id === active.id);
    const newIndex = filteredHabits.findIndex((h) => h.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedFiltered = arrayMove(filteredHabits, oldIndex, newIndex);
    const groupHabitIds = new Set(reorderedFiltered.map(h => h.id));
    const allHabitsSorted = [...habits].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const completeReorderedList: Habit[] = [];
    let reorderedIndex = 0;
    
    for (const habit of allHabitsSorted) {
      if (groupHabitIds.has(habit.id)) {
        completeReorderedList.push(reorderedFiltered[reorderedIndex]);
        reorderedIndex++;
      } else {
        completeReorderedList.push(habit);
      }
    }
    
    const habitIds = completeReorderedList.map((h) => h.id);
    reorderHabitsMutation.mutate(habitIds);
  };

  if (habitsLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading your habits...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Active Habits",
      value: habitStats?.totalHabits || 0,
      icon: Target,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Completed Today",
      value: habitStats?.completedToday || 0,
      icon: TrendingUp,
      color: "text-green-600",
      bgColor: "bg-success/10",
    },
    {
      title: "Longest Streak",
      value: `${habitStats?.longestStreak || 0} days`,
      icon: Flame,
      color: "text-orange-500",
      bgColor: "bg-accent/10",
    },
    {
      title: "Completion Rate",
      value: `${habitStats?.completionRate || 0}%`,
      icon: Calendar,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
  ];

  return (
    <>
          {/* Header */}
          <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                  Habit Tracker 📈
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Build consistency with{" "}
                  <span className="font-medium text-accent">
                    {habits.filter(h => h.isActive).length} active habits
                  </span>{" "}
                  and track your daily progress.
                </p>
              </div>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground w-full sm:w-auto"
                data-testid="button-create-habit"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Habit
              </Button>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 py-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card key={stat.title} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-muted-foreground text-sm font-medium">
                            {stat.title}
                          </p>
                          <p className="text-2xl font-bold text-foreground mt-1" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                            {stat.value}
                          </p>
                        </div>
                        <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              <Card className="hover:shadow-md transition-shadow" data-testid="card-trends">
                <CardContent className="p-6 flex flex-col justify-between h-full">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-muted-foreground text-sm font-medium">Consistency</p>
                      <p className="text-2xl font-bold text-foreground mt-1" data-testid="stat-consistency">
                        {consistencyPercent}%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-purple-500" />
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    onClick={() => {
                      setShowTrends(true);
                      setHabitsInitialized(false);
                      queryClient.invalidateQueries({ queryKey: ["/api/habits/trends"], exact: false });
                    }}
                    data-testid="button-show-trends"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Trends
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Habits List */}
            <div className="mb-8">
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg sm:text-xl font-bold text-foreground">Your Habits</h2>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <PartyPopper className={`w-4 h-4 ${celebrationEnabled ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                      <Label htmlFor="celebration-toggle" className="text-sm cursor-pointer whitespace-nowrap">
                        Celebrations
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowCelebrationHelp(true)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="button-help-celebration"
                      >
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      <Switch
                        id="celebration-toggle"
                        checked={celebrationEnabled}
                        onCheckedChange={handleCelebrationToggle}
                        data-testid="switch-celebration-toggle"
                      />
                    </div>

                    <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && setViewMode(value as "grid" | "list")} data-testid="toggle-view-mode">
                      <ToggleGroupItem value="grid" aria-label="Grid view" data-testid="toggle-grid-view">
                        <Grid3x3 className="h-4 w-4" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="list" aria-label="List view" data-testid="toggle-list-view">
                        <List className="h-4 w-4" />
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  {/* Streak Period Selector */}
                  <Select value={streakPeriod} onValueChange={(value) => setStreakPeriod(value as any)}>
                    <SelectTrigger className="w-full sm:w-40" data-testid="select-streak-period">
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="semi-annually">Semi-Annually</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Category Filter — dynamic, includes goals-scoped categories */}
                  <Select value={selectedCategory} onValueChange={(v) => {
                    setSelectedCategory(v);
                    try { localStorage.setItem(userLsKey("habitsCategory", userId), v); } catch {}
                  }}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="select-category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <CategorySelectGroups categories={allCategories} getValue={(c) => c.name!.toLowerCase()} />
                    </SelectContent>
                  </Select>

                  {/* Group By */}
                  <Select value={groupBy} onValueChange={(v) => {
                    const val = v as "none" | "category";
                    setGroupBy(val);
                    try { localStorage.setItem(userLsKey("habitsGroupBy", userId), val); } catch {}
                  }} data-testid="select-group-by">
                    <SelectTrigger className="w-full sm:w-44" data-testid="select-group-by-trigger">
                      <Layers className="w-4 h-4 mr-2 text-muted-foreground" />
                      <SelectValue placeholder="Group By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Grouping</SelectItem>
                      <SelectItem value="category">Group by Category</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Habits Grid or List — grouped by category or flat */}
              {groupBy === "category" && groupedHabits ? (
                groupedHabits.size === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">No habits found</h3>
                    <p className="text-muted-foreground mb-4">
                      {selectedCategory === "all"
                        ? "Start building consistent habits by creating your first one!"
                        : "No habits in this category yet."}
                    </p>
                    <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-create-first-habit">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Habit
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-8" data-testid="habits-grouped-view">
                    {[...groupedHabits.entries()].map(([category, categoryHabits]) => (
                      <div key={category}>
                        {/* Category group header */}
                        <div className="flex items-center gap-3 mb-4">
                          <h3 className="font-semibold text-base text-foreground capitalize" data-testid={`group-header-${category.toLowerCase()}`}>
                            {category}
                          </h3>
                          <Badge variant="secondary" className="rounded-full px-2 py-0.5 text-xs">
                            {categoryHabits.length}
                          </Badge>
                          <div className="flex-1 h-px bg-border" />
                        </div>

                        {viewMode === "list" ? (
                          <div className="space-y-4">
                            {categoryHabits.map((habit) => (
                              <HabitCard key={habit.id} habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {categoryHabits.map((habit) => (
                              <HabitCard key={habit.id} habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <>
                  {/* Flat view with DnD (disabled on mobile for better touch support) */}
                  {viewMode === "list" ? (
                    isMobile ? (
                      <div className="space-y-4">
                        {filteredHabits.map((habit) => (
                          <HabitCard key={habit.id} habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
                        ))}
                      </div>
                    ) : (
                      // Desktop: Full drag-and-drop support
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={filteredHabits.map((h) => h.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-4">
                            {filteredHabits.map((habit) => (
                              <SortableHabitItem key={habit.id} habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {filteredHabits.map((habit) => (
                        <HabitCard key={habit.id} habit={habit} viewMode={viewMode} celebrationEnabled={celebrationEnabled} />
                      ))}
                    </div>
                  )}

                  {filteredHabits.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Target className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground mb-2">No habits found</h3>
                      <p className="text-muted-foreground mb-4">
                        {selectedCategory === "all"
                          ? "Start building consistent habits by creating your first one!"
                          : "No habits in this category yet."}
                      </p>
                      <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-create-first-habit">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Habit
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {archivedHabits.length > 0 && (
              <div className="mt-8">
                <Button variant="ghost" onClick={() => setShowArchived(!showArchived)} className="mb-4">
                  {showArchived ? "Hide" : "Show"} Archived Habits ({archivedHabits.length})
                </Button>
                {showArchived && (
                  <div className="space-y-3 opacity-60">
                    {archivedHabits.map(habit => (
                      <Card key={habit.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">{habit.name}</h3>
                            <p className="text-sm text-muted-foreground">{habit.category} · {habit.frequency} · Target: {habit.target}</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => restoreHabitMutation.mutate(habit.id)}>
                            Restore
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">💡 Habit Building Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Start Small</h4>
                    <p className="text-muted-foreground">Begin with tiny habits that take less than 2 minutes to complete.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Stack Habits</h4>
                    <p className="text-muted-foreground">Link new habits to existing routines for better consistency.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Track Daily</h4>
                    <p className="text-muted-foreground">Use this tracker to maintain awareness and build momentum.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Be Patient</h4>
                    <p className="text-muted-foreground">It takes an average of 66 days to form a new habit.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

      <CreateHabitModal 
        open={isCreateModalOpen} 
        onOpenChange={setIsCreateModalOpen} 
      />

      <Dialog open={showTrends} onOpenChange={setShowTrends}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Habit Trends
            </DialogTitle>
            <DialogDescription>Track your habit consistency over time. Lines rise with consecutive completions and drop to zero on missed days.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-4">
            <Label className="text-sm font-medium whitespace-nowrap">Time Range:</Label>
            <Select value={String(trendsDays)} onValueChange={(v) => { setTrendsDays(parseInt(v)); setHabitsInitialized(false); }}>
              <SelectTrigger className="w-40" data-testid="select-trends-days">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {trendChartData.length > 0 ? (
            <div className="w-full" style={{ height: isMobile ? 300 : 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    interval={trendsDays <= 14 ? 0 : trendsDays <= 30 ? 2 : 5}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? "end" : "middle"}
                    height={isMobile ? 60 : 30}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Streak', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                    formatter={(value: number, name: string) => {
                      const habit = trendsData.find(h => h.habitId === name);
                      return [value, habit?.habitName || name];
                    }}
                  />
                  {trendsData.filter(h => enabledHabits.has(h.habitId)).map((habit, idx) => (
                    <Line
                      key={habit.habitId}
                      type="monotone"
                      dataKey={habit.habitId}
                      name={habit.habitId}
                      stroke={TREND_COLORS[idx % TREND_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-muted-foreground">
              <p>No trend data available yet. Start completing habits to see trends.</p>
            </div>
          )}

          <div className="mt-4">
            <p className="text-sm font-medium text-foreground mb-3">Active Habits</p>
            <div className="flex flex-wrap gap-2">
              {trendsData.map((habit, idx) => {
                const isEnabled = enabledHabits.has(habit.habitId);
                const color = TREND_COLORS[idx % TREND_COLORS.length];
                return (
                  <Badge
                    key={habit.habitId}
                    variant={isEnabled ? "default" : "outline"}
                    className="cursor-pointer select-none transition-all text-sm px-3 py-1.5"
                    style={isEnabled ? { backgroundColor: color, borderColor: color, color: '#fff' } : { borderColor: color, color: color }}
                    onClick={() => toggleHabitTrend(habit.habitId)}
                    data-testid={`badge-trend-${habit.habitId}`}
                  >
                    {habit.habitName}
                  </Badge>
                );
              })}
            </div>
          </div>

          <Button onClick={() => setShowTrends(false)} className="w-full mt-4">Close</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showCelebrationHelp} onOpenChange={setShowCelebrationHelp}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>What are Celebrations?</DialogTitle>
            <DialogDescription>Understanding the habit streak celebration feature</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-foreground">
            <p>
              <strong>Celebrations</strong> reward your consistency with a confetti animation when you build a streak of completing a habit.
            </p>
            <div className="space-y-2">
              <p className="font-semibold">How it works:</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Complete a habit for <strong>3 or more consecutive days</strong> (or periods, depending on frequency)</li>
                <li>A confetti celebration will appear on screen to acknowledge your streak</li>
                <li>The longer your streak, the more reason to celebrate!</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-semibold">Toggle on or off:</p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><strong>On:</strong> You'll see confetti when you hit 3+ consecutive completions</li>
                <li><strong>Off:</strong> No celebration animations - completions are tracked silently</li>
              </ul>
            </div>
            <p className="text-muted-foreground italic">
              Tip: Celebrations are a great motivator! Seeing confetti after a streak reinforces the positive behavior and keeps you going.
            </p>
          </div>
          <Button onClick={() => setShowCelebrationHelp(false)} className="w-full mt-2">Got it!</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
